import { DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { AlertType, Modal } from '@toggle/design-system';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCustomSources } from '~/react-query-hooks/alfred/useCustomSources';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { ToastPosition, useToasts } from '~/stores/use-toasts/useToasts';
import {
  updateCustomSources,
  WebSearch,
} from '~/views/alfred/research/services/conversations-service';

import { formatWebsiteInfo, WebsiteInfo } from '../utils/formatWebsiteInfo';
import { InlineAlert } from './InlineAlert';
import { LinkInput } from './LinkInput';
import { DroppableArea, MAX_CUSTOM_SOURCES } from './SourcesDraggableArea';
import { SourcesUpdateAlert } from './SourcesUpdateAlert';
import styles from './TrustedSourcesModal.module.scss';
import { ModalOpenFrom } from './WebSearchDropdown';

const BLOCKED_ERROR_CODE = 'SETTING_WEBSEARCH_SOURCES_BLOCKED';

interface TrustedSourcesModalProps {
  isOpen: ModalOpenFrom;
  onClose: () => void;
  setWebSearch: (webSearch: WebSearch) => void;
}

export const TrustedSourcesModal = ({
  isOpen,
  onClose,
  setWebSearch,
}: TrustedSourcesModalProps) => {
  const { t } = useTranslation(['chat', 'common']);

  const [customSources, setCustomSources] = useState<WebsiteInfo[]>([]);
  const [restrictedErrors, setRestrictedErrors] = useState<string[] | null>(
    null
  );

  const { trackAlfredTrustedSourceAdded, trackAlfredTrustedSourceRemoved } =
    usePosthogEvents();
  const showToast = useToasts(state => state.showToast);

  const handleAddWebsites = (websites: WebsiteInfo[]) => {
    const existedSources = customSources.map(source => source.url);
    const uniqueWebsites = websites.filter(
      website => !existedSources.includes(website.url)
    );

    if (uniqueWebsites.length > 0) {
      trackAlfredTrustedSourceAdded(POSTHOG_TRACKING_SOURCES.ALFRED);
      const newSources = [...customSources, ...uniqueWebsites];
      setCustomSources(newSources);
    }
  };

  const handleClose = () => {
    setCustomSources([]);
    setRestrictedErrors(null);
    onClose();
  };

  const handleUpdateCustomSources = async () => {
    const response = await updateCustomSources({
      sources: customSources.map(source => source.url),
    });

    if (response.error?.message) {
      const errorBody = JSON.parse(response.error?.message);

      if (errorBody.code === BLOCKED_ERROR_CODE) {
        setRestrictedErrors(errorBody.blocked_sources);
        setCustomSources(
          customSources.filter(
            source => !errorBody.blocked_sources.includes(source.url)
          )
        );
      } else {
        showToast({
          id: 'update-sources-error',
          position: ToastPosition.TopRight,
          content: (
            <SourcesUpdateAlert
              type={AlertType.Error}
              message={t('chat:trustedSources.updateError')}
            />
          ),
        });
      }

      return;
    }

    if (response) {
      handleClose();
    }

    if (response.data) {
      await refetchCustomSources();

      if (isOpen === 'option') {
        setWebSearch('restricted');
      }

      showToast({
        id: 'update-sources-success',
        position: ToastPosition.TopRight,
        content: (
          <SourcesUpdateAlert
            type={AlertType.Success}
            message={t('chat:trustedSources.updateSuccess')}
          />
        ),
      });
    }
  };

  const handleRemoveSource = (id: string) => {
    trackAlfredTrustedSourceRemoved(POSTHOG_TRACKING_SOURCES.ALFRED);
    setCustomSources(customSources.filter(source => source.id !== id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over) {
      setCustomSources(currentSources => {
        const activeIndex = currentSources.findIndex(
          source => source.id === active.id
        );
        const overIndex = currentSources.findIndex(
          source => source.id === over.id
        );

        if (activeIndex === -1 || overIndex === -1) {
          return currentSources;
        }

        return arrayMove(currentSources, activeIndex, overIndex);
      });
    }
  };

  const { data: customSourcesResponse, refetch: refetchCustomSources } =
    useCustomSources();

  const hasUserModified = useMemo(() => {
    const originalSources = customSourcesResponse?.data?.sources ?? [];
    const currentSources = customSources.map(source => source.url);

    if (originalSources.length !== currentSources.length) {
      return true;
    }

    return originalSources.some((url, index) => url !== currentSources[index]);
  }, [customSources, customSourcesResponse]);

  useEffect(() => {
    if (customSourcesResponse?.data?.sources) {
      setCustomSources(formatWebsiteInfo(customSourcesResponse.data.sources));
    }
  }, [customSourcesResponse]);

  const getModalProps = () => {
    return {
      title: t('chat:trustedSources.modal.title'),
      description: (
        <p>
          {t('chat:trustedSources.modal.description_1')}
          <br />
          <br />
          {t('chat:trustedSources.modal.description_2')}
        </p>
      ),
      ...(hasUserModified && {
        primaryBtnProps: {
          label: t('chat:trustedSources.modal.saveTrustedSources'),
          onClick: handleUpdateCustomSources,
        },
        cancelBtnProps: {
          label: t('common:cancel'),
          onClick: handleClose,
        },
      }),
    };
  };

  return (
    <Modal
      className={styles.styledModal}
      isOpen={isOpen !== undefined}
      onClose={handleClose}
      {...getModalProps()}
      disableEnterClose
      outsideClickClose={false}
      data-testid="trusted-sources-modal"
    >
      <div className={styles.content}>
        <div className={styles.sources}>
          <DndContext onDragEnd={handleDragEnd}>
            <SortableContext
              items={customSources.map(source => source.id)}
              strategy={verticalListSortingStrategy}
            >
              <DroppableArea
                items={customSources}
                handleRemove={handleRemoveSource}
              />
            </SortableContext>
          </DndContext>
        </div>
        {!!restrictedErrors?.length && (
          <InlineAlert
            type={AlertType.Error}
            message={t('chat:trustedSources.restrictedError', {
              count: restrictedErrors.length,
              domains: restrictedErrors?.join(', '),
            })}
          />
        )}
        {customSources.length >= MAX_CUSTOM_SOURCES ? (
          <InlineAlert alertType="exceededLimit" />
        ) : (
          <LinkInput
            sourceCount={customSources.length}
            handleAddWebsites={handleAddWebsites}
            handleResetRestrictedErrors={() => setRestrictedErrors(null)}
          />
        )}
        <p className={styles.disclaimerText}>
          {t('chat:trustedSources.disclaimer')}
        </p>
      </div>
    </Modal>
  );
};
